import React, { ChangeEvent, ComponentType } from 'react';
import { compose } from 'recompose';
import { Trans } from 'react-i18next';
import Auth from '@aws-amplify/auth';
import { ConfirmSignUp as AmplifyConfirmSignUp } from 'aws-amplify-react/lib/Auth';
import { Button, Typography } from '@material-ui/core';
import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Formik, FieldProps, Form } from 'formik';

import { AuthState, ICognitoError } from 'types/aws-amplify';
import { Login } from 'Links';
import { onboardingProfile } from 'paths';
import storage from 'storage';
import { withClient, withNotify, IWithClient, IWithNotify } from 'providers';
import {
  ActionContainer,
  ClientLogo,
  Logo,
  SubmitButton,
  Layouts,
  ValidatedTextField,
} from 'components';

import { Main } from '../components';
import { ConfirmSignUpSchema, IConfirmSignUpValues } from '../validations';

interface IPropsEnhanced
  extends IWithNotify,
    IWithClient,
    WithStyles<typeof styles>,
    RouteComponentProps {}

interface IState {
  working: boolean;
  resent: boolean;
}

const styles = (theme: Theme) =>
  createStyles({
    button: {
      padding: 0,
      marginTop: theme.spacing(1),
    },
  });

const enhancers = compose<IPropsEnhanced, {}>(
  withClient,
  withRouter,
  withStyles(styles),
  withNotify,
);

class ConfirmSignUp extends AmplifyConfirmSignUp<IPropsEnhanced, IState> {
  state = {
    working: false,
    resent: false,
  };

  onSubmit = async (values: IConfirmSignUpValues) => {
    const { history } = this.props;
    Object.assign(this.inputs, values);

    const { code } = this.inputs;
    this.setState({ working: true });

    Auth.confirmSignUp(storage.userEmail, code)
      .then(this.signIn)
      .then(() => this.changeState(AuthState.signedIn))
      .then(() => history.push(onboardingProfile))
      .catch(this.error);
  };

  handleResend = () => {
    Object.assign(this.inputs, { username: storage.userEmail });

    this.setState({ resent: true });
    this.resend();
  };

  error = (error: ICognitoError) => {
    const { notify } = this.props;

    const message = this.errorMessage(error);
    this.setState({ working: false });
    notify.error({ message });
  };

  signIn = () => {
    const { authData } = this.props;
    const { username, password } = authData;

    return Auth.signIn(username, password);
  };

  renderSidebar() {
    const { classes, client } = this.props;
    const { resent, working } = this.state;

    const LogoComponent: ComponentType = client ? ClientLogo : Logo;
    const initialValues: IConfirmSignUpValues = {
      code: '',
    };

    return (
      <Formik
        onSubmit={this.onSubmit}
        initialValues={initialValues}
        validationSchema={ConfirmSignUpSchema}>
        {() => (
          <Form>
            <LogoComponent />
            <Typography gutterBottom variant="h4" color="textSecondary">
              <Trans i18nKey="auth:validateSidebarHeader" />
            </Typography>

            <ValidatedTextField
              name="code"
              i18nKey="auth:validateLabel"
              onChange={(
                event: ChangeEvent<HTMLInputElement>,
                onChange: FieldProps['field']['onChange'],
              ) => {
                event.target.value = (event.target.value || '').trim();
                onChange(event);
              }}
            />

            <Button className={classes.button} onClick={this.handleResend}>
              {resent ? (
                <Trans i18nKey="auth:validateResent" />
              ) : (
                <Trans i18nKey="auth:validateResendCode" />
              )}
            </Button>

            <ActionContainer>
              <SubmitButton working={working} text={<Trans i18nKey="auth:validateButton" />} />
              <Button component={Login} fullWidth>
                <Trans i18nKey="auth:backToLogin" />
              </Button>
            </ActionContainer>
          </Form>
        )}
      </Formik>
    );
  }

  showComponent() {
    return (
      <Layouts.Full2Column
        sidebar={this.renderSidebar()}
        main={<Main headerI18nKey="auth:validateSuccess" />}
      />
    );
  }
}

// @ts-ignore - TODO: AWS Components are untyped
export default enhancers(ConfirmSignUp);
